import { FC, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  GreyGrey30,
  IconPlus,
  Button,
  Box,
  Flex
} from '@sequencehq/core-components'
import { MobileNavToggle } from 'components/NavigationV3/components/MobileNavToggle/MobileNavToggle'

export type AddNewConfig =
  | {
      buttonText?: string
      href: string
    }
  | {
      buttonText?: string
      onClick: () => void
    }
  | {
      button: ReactNode
    }

const isHrefConfig = (
  addNewConfig: AddNewConfig
): addNewConfig is { href: string } => {
  return 'href' in addNewConfig
}

const isOnClickConfig = (
  addNewConfig: AddNewConfig
): addNewConfig is { onClick: () => void } => {
  return 'onClick' in addNewConfig
}

const isButtonConfig = (
  addNewConfig: AddNewConfig
): addNewConfig is { button: ReactNode } => {
  return 'button' in addNewConfig
}

type TopNavigationProps = {
  children: ReactNode
  url?: string
  actionElement?: ReactNode
  addNewConfig?: AddNewConfig
}

const TopNavigation: FC<TopNavigationProps> = ({
  children,
  addNewConfig,
  actionElement
}) => {
  const navigate = useNavigate()

  const handleClick = () => {
    if (!addNewConfig) {
      return
    }

    if (isHrefConfig(addNewConfig)) {
      navigate(addNewConfig.href)
      return
    }

    if (isOnClickConfig(addNewConfig)) {
      addNewConfig.onClick()
    }
  }

  const style = {
    height: '48px',
    borderBottom: `${GreyGrey30} 1px solid`,
    px: '8px',
    py: '16px'
  }

  return (
    <Flex
      w="100%"
      backgroundColor="transparent"
      justify="space-between"
      align="center"
      position="static"
      gap="8px"
      sx={style}
    >
      {/* Leads the header on mobile so every page built on `Page` can reopen the
          nav drawer; hides itself on desktop where the sidebar is always
          visible. */}
      <MobileNavToggle />
      {children}
      {(actionElement || addNewConfig) && <Box width={'8px'} />}
      {actionElement ? (
        actionElement
      ) : addNewConfig ? (
        isButtonConfig(addNewConfig) ? (
          addNewConfig.button
        ) : (
          <Button
            variant="primary"
            leadingIcon={<IconPlus color="inherit" />}
            onClick={handleClick}
            data-testid="TopNavigation.add-new"
          >
            {addNewConfig.buttonText ?? 'Add new'}
          </Button>
        )
      ) : null}
    </Flex>
  )
}

export default TopNavigation
